/**
 * Readers for plan values that already exist in the Proposal object.
 *
 * THE RULE: the client performs no commercial arithmetic. It looks values up
 * and formats them. If a number isn't in state, the block omits the row rather
 * than deriving it — a plausible-looking total the engines didn't produce is
 * worse than a missing one.
 *
 * The Proposal has no top-level priced plan; pricing lives on the active
 * budget tier (`budget.tiers[activeTierId].pricing`), which is where totals
 * are read from when the server has produced them.
 */
import type { PageRef } from '@shared/api';
import type {
  ChannelAllocation,
  ChannelForecast,
  DocumentSection,
  MediaStrategy,
  PlanTier,
  PricedLine,
  PricedPlan,
  PricingModel,
  Product,
  ProductFamily,
  Proposal,
  TacticStrategy,
} from '@shared/types';
import { count, formatDate, humanizeId, money } from './format';

/**
 * Delivery kind a line's numbers are read off of, widened (V16 rate cards)
 * beyond the original three models: 'cpv' has its own delivered-units field;
 * 'managed' and 'scoped' never populate one (managed prices as a fee on
 * media spend, scoped is a quoted flat rate) so they're only distinguishable
 * via the product's own pricing model, not the line's populated fields.
 */
export type DeliveryKind = 'cpm' | 'cpc' | 'cpv' | 'flat' | 'managed' | 'scoped' | null;

/** The catalog product backing a plan line: the proposal's pinned rate-card snapshot, then its rep-added channels. Null pre-rate-cards or off-snapshot. */
export function productForLine(proposal: Proposal, productId: string): Product | null {
  return (
    proposal.rateCard?.catalog.products.find((product) => product.productId === productId) ??
    proposal.repProducts?.find((product) => product.productId === productId) ??
    null
  );
}

/** A line's family, read off its rate-card snapshot. Null when there is no snapshot (legacy/default-catalog proposal) or the id isn't on it. */
export function productFamily(proposal: Proposal, productId: string): ProductFamily | null {
  return productForLine(proposal, productId)?.family ?? null;
}

/**
 * The generic-catalog productId whose artwork (device art, channel hero
 * mockup, legend icon) a line borrows. Slide art is keyed by the generic
 * catalog's ids (`paid_search`, `targeted_display`, …), but a rate card's
 * ids are the org's own (Pamal's `sem`, `display`, `youtube_trueview`), so
 * the family on the proposal's snapshot picks the art. A legacy proposal (no
 * snapshot) or a family with no art of its own (`dooh`) keeps its own id,
 * which then falls through to the template's fallback art.
 */
const ART_PRODUCT_BY_FAMILY: Partial<Record<ProductFamily, string>> = {
  search: 'paid_search',
  social: 'social',
  ctv: 'ctv',
  online_video: 'online_video',
  display: 'targeted_display',
  mobile_location: 'mobile_location',
  streaming_audio: 'streaming_audio',
  email: 'email',
};

export function artProductId(proposal: Proposal, productId: string): string {
  const family = productFamily(proposal, productId);
  return (family && ART_PRODUCT_BY_FAMILY[family]) || productId;
}

/** A line's pricing model, read off its rate-card snapshot. Null under the same conditions as productFamily. */
export function productPricingModel(proposal: Proposal, productId: string): PricingModel | null {
  return productForLine(proposal, productId)?.pricing.model ?? null;
}

/**
 * Which pricing model priced a line, read off which delivery field is
 * populated (cpm → impressions, cpc → clicks, cpv → views, flat → units)
 * rather than from a model field the client never receives. Null when the
 * line hasn't been priced yet and its model isn't known either.
 *
 * managed/scoped never populate a delivery field even once priced (managed
 * has no delivery unit at all; scoped is a quoted flat rate with no units
 * on file) — pass `model` (see productPricingModel) when the caller needs
 * those two kinds distinguished from "unpriced, unknown model".
 */
export function deliveryKind(
  line: PricedLine | null,
  forecast?: ChannelForecast | null,
  model?: PricingModel | null,
): DeliveryKind {
  // The priced line is authoritative, but it only exists once tiers do —
  // recalc parks pricing on the active tier. A tier-less proposal still has a
  // persisted forecast, whose populated field names the model the same way.
  if (line) {
    if (line.estImpressionsMonthly !== null) return 'cpm';
    if (line.estClicksMonthly !== null) return 'cpc';
    if (line.estViewsMonthly !== null) return 'cpv';
    if (line.estUnitsMonthly !== null) return 'flat';
  }
  if (forecast) {
    if (forecast.impressionsMonthly !== null) return 'cpm';
    if (forecast.clicksMonthly !== null && forecast.clicksMonthly !== undefined) return 'cpc';
  }
  // managed/scoped: no field above is ever populated, priced or not — the
  // model itself is the only signal.
  if (model === 'managed' || model === 'scoped') return model;
  return null;
}

export function activeTier(proposal: Proposal): PlanTier | null {
  const { tiers, activeTierId } = proposal.budget;
  if (!activeTierId) return null;
  return tiers.find((tier) => tier.id === activeTierId) ?? null;
}

export function pricedPlan(proposal: Proposal): PricedPlan | null {
  return activeTier(proposal)?.pricing ?? null;
}

export function pricedLine(proposal: Proposal, productId: string): PricedLine | null {
  return pricedPlan(proposal)?.lines.find((line) => line.productId === productId) ?? null;
}

export function channel(proposal: Proposal, productId: string): ChannelAllocation | null {
  return proposal.mediaPlan.channels.find((line) => line.productId === productId) ?? null;
}

export function channelForecast(proposal: Proposal, productId: string): ChannelForecast | null {
  return proposal.forecast.byChannel.find((line) => line.productId === productId) ?? null;
}

/**
 * V12: one channel's delivery numbers, gathered exactly the way
 * ProductPlanPage already does per pricing model (design.md V12 tactic_
 * measurement: "reuse ProductPlanPage's bound metric logic") — the forecast
 * is authoritative, the priced line is the fallback, and nothing here is
 * computed beyond picking which already-stored field applies. Consumed by
 * the new tactic_measurement archetype (both families) so it never
 * re-derives what ProductPlanPage already reads correctly.
 */
export interface TacticDeliveryMetrics {
  kind: DeliveryKind;
  /** True once the line has a real price (base card or rep override) — never priced, never a placeholder number. */
  priced: boolean;
  monthlyBudget: number | null;
  impressions: number | null;
  clicks: number | null;
  /** cpv only. */
  views: number | null;
  percentOfOpportunity: number | null;
  units: number | null;
  unitLabel: string | null;
  /** managed only: the fee already computed by the pricing engine (never re-derived here). */
  feeMonthly: number | null;
  /** flat: the single drop date; every other model: the flight's full date range. */
  dateLabel: string | null;
}

export function tacticDeliveryMetrics(proposal: Proposal, productId: string): TacticDeliveryMetrics {
  const line = channel(proposal, productId);
  const priced = pricedLine(proposal, productId);
  const fc = channelForecast(proposal, productId);
  const model = productPricingModel(proposal, productId);
  const kind = deliveryKind(priced, fc, model);
  return {
    kind,
    priced: priced?.priced ?? false,
    monthlyBudget: line?.monthlyBudget ?? null,
    impressions: fc?.impressionsMonthly ?? priced?.estImpressionsMonthly ?? null,
    clicks: fc?.clicksMonthly ?? priced?.estClicksMonthly ?? null,
    views: priced?.estViewsMonthly ?? null,
    percentOfOpportunity: fc?.percentOfOpportunity ?? null,
    units: priced?.estUnitsMonthly ?? null,
    unitLabel: priced?.unitLabel ?? null,
    feeMonthly: kind === 'managed' ? (priced?.monthlyFees ?? null) : null,
    // 'flat' is the one model whose delivery is a single drop (e.g. an
    // email send) rather than something running across the flight — cpv,
    // managed and scoped are all ongoing, same as cpm/cpc.
    dateLabel: kind === 'flat' ? formatDate(proposal.budget.startDate) : flightDateRange(proposal),
  };
}

/**
 * F13: a channel that IS part of the media plan but whose delivery `kind`
 * resolves to null (no priced line, no usable forecast, and not a
 * managed/scoped model) has nothing for a rail to show — e.g. proposal
 * c5b885d3's meta_advertising line, whose ChannelForecast came back with
 * every field null while every other channel forecast fine. The product-plan
 * rail components (StatTile) render nothing at all for a null value, so this
 * silently read as a blank rather than "no forecast" — indistinguishable
 * from a page still loading. Callers use this to render an explicit
 * "No forecast yet" tile instead.
 */
export function forecastUnavailable(proposal: Proposal, productId: string, metrics: TacticDeliveryMetrics): boolean {
  return metrics.kind === null && channel(proposal, productId) != null;
}

/**
 * One short "$X/mo · key metric" line for a tactic_group card (design.md
 * V12: "bound $X/mo · key metric row") — the single delivery number that
 * best represents the pricing model, formatting only. Null pieces are
 * omitted rather than shown as a dash, so a not-yet-priced line still shows
 * its budget alone. managed has no delivery unit at all (design.md V16) —
 * its fee is the closest thing, shown instead.
 */
export function tacticGroupMetricLine(metrics: TacticDeliveryMetrics): string | null {
  const parts: string[] = [];
  const budget = money(metrics.monthlyBudget);
  if (budget) parts.push(`${budget}/mo`);
  if (!metrics.priced) {
    parts.push('pending price');
  } else if (metrics.kind === 'cpm' && metrics.impressions !== null) {
    parts.push(`${count(metrics.impressions)} impr./mo`);
  } else if (metrics.kind === 'cpc' && metrics.clicks !== null) {
    parts.push(`${count(metrics.clicks)} clicks/mo`);
  } else if (metrics.kind === 'cpv' && metrics.views !== null) {
    parts.push(`${count(metrics.views)} views/mo`);
  } else if (metrics.kind === 'flat' && metrics.units !== null) {
    parts.push(`${count(metrics.units)} ${metrics.unitLabel ?? 'units'}/mo`);
  } else if (metrics.kind === 'managed' && metrics.feeMonthly !== null) {
    parts.push(`${money(metrics.feeMonthly)} fee/mo`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Sum of monthlyBudget across every plan channel — the "CURRENT allocated
 * total" `rebalance_channel` redistributes against (design.md V11 item 5).
 * Display-only: the budget-chip popover uses it purely to preview a dollar
 * value before Apply; the mutation itself recomputes this server-side.
 */
export function allocatedTotal(proposal: Proposal): number {
  return proposal.mediaPlan.channels.reduce((sum, line) => sum + line.monthlyBudget, 0);
}

/**
 * V16: a caption for a grand total that's real but partial — every dollar in
 * `plan.grandTotal` etc. is a genuine number (unpriced lines contribute real
 * budget, zero fees), but it's missing the fees/vendor cost a still-unpriced
 * line would add once priced. Null when the plan is complete (nothing to
 * caveat) or doesn't exist yet.
 */
export function partialTotalsNote(plan: PricedPlan | null): string | null {
  if (!plan || plan.complete) return null;
  // Plans stored before V16 have neither flag; an absent id list is nothing to caveat.
  const n = plan.unpricedProductIds?.length ?? 0;
  if (n === 0) return null;
  return `Excludes ${n} unpriced ${n === 1 ? 'item' : 'items'}`;
}

// ---------------------------------------------------------------------------
// Media strategy (V10) readers — same rule: look up and format, never derive
// a commercial value. Budget %, dollars and totals a tactic header or the Rep
// Guide shows always come from the plan (above), joined against these.
// ---------------------------------------------------------------------------

/** The media strategy layer. Always present on a hydrated Proposal (empty until authored). */
export function strategyFor(proposal: Proposal): MediaStrategy {
  return proposal.strategy;
}

/** The strategy entry for one tactic, keyed by catalog productId. Null when not yet authored. */
export function tacticFor(proposal: Proposal, productId: string): TacticStrategy | null {
  return proposal.strategy.tactics.find((tactic) => tactic.productId === productId) ?? null;
}

/**
 * Short, presentation-only strategic role for a channel that has no authored
 * `TacticStrategy` entry yet — used ONLY where a role would otherwise render
 * and none exists (design.md V10 item 6). Known channel ids map explicitly;
 * anything else falls back on its pricing model, mirroring `journeyStage`'s
 * fallback shape. Never a commercial value, never persisted.
 */
const DEFAULT_TACTIC_ROLE_BY_PRODUCT: Record<string, string> = {
  ctv: 'Create broad awareness',
  online_video: 'Build brand awareness',
  streaming_audio: 'Extend awareness reach',
  social: 'Build consideration',
  targeted_display: 'Stay top of mind',
  mobile_location: 'Reach nearby intent',
  paid_search: 'Capture existing demand',
  email: 'Retain past customers',
};

/**
 * V16: family-keyed fallback for a rate-card product whose id isn't one of
 * the generic catalog's own (a Pamal `twitch` never matches `ctv` above, but
 * its family does) — checked before the kind-based default below.
 */
const DEFAULT_TACTIC_ROLE_BY_FAMILY: Partial<Record<ProductFamily, string>> = {
  ctv: 'Create broad awareness',
  online_video: 'Build brand awareness',
  streaming_audio: 'Extend awareness reach',
  social: 'Build consideration',
  display: 'Stay top of mind',
  mobile_location: 'Reach nearby intent',
  search: 'Capture existing demand',
  email: 'Retain past customers',
  dooh: 'Create broad awareness',
  web_services: 'Deliver a scoped project',
};

export function defaultTacticRole(productId: string, kind: DeliveryKind, family?: ProductFamily | null): string {
  const known = DEFAULT_TACTIC_ROLE_BY_PRODUCT[productId] ?? (family ? DEFAULT_TACTIC_ROLE_BY_FAMILY[family] : undefined);
  if (known) return known;
  if (kind === 'cpc') return 'Capture existing demand';
  if (kind === 'cpv') return 'Build brand awareness';
  if (kind === 'flat') return 'Retain past customers';
  if (kind === 'managed') return 'Run a delegated program';
  if (kind === 'scoped') return 'Deliver a scoped project';
  return 'Build consideration';
}

/**
 * Monthly impressions for a channel: the forecast is authoritative, the priced
 * line is the fallback. Never computed here.
 */
export function impressionsFor(proposal: Proposal, productId: string): number | null {
  const forecast = channelForecast(proposal, productId);
  if (forecast && forecast.impressionsMonthly !== null) return forecast.impressionsMonthly;
  return pricedLine(proposal, productId)?.estImpressionsMonthly ?? null;
}

const PRODUCT_SECTION_TYPES = new Set([
  'media_product',
  'product_divider',
  'product_education',
  'product_plan',
  // V12: a tactic's extra standalone pages (design.md "Assembly semantics" —
  // a tactic's pages render consecutively in its `pages` order) share the
  // same single `productId`, so they cluster into the same PageGroup exactly
  // like the V5 bundle triple does — the workspace's tactic-card grouping
  // (GridWorkspace/TacticCard) needs no changes beyond this membership.
  'tactic_audience',
  'tactic_moment',
  'tactic_measurement',
]);

/**
 * The name to show for a channel. The document's own product-bundle section
 * title wins (the rep may have renamed it); otherwise humanize the id. Legacy
 * `media_product` and the V5 `product_plan` page both carry a title, so either
 * one answers. The product catalog lives on the server, so there is nothing
 * else to consult.
 */
export function productLabel(proposal: Proposal, productId: string): string {
  const section = proposal.document?.sections.find(
    (candidate) =>
      PRODUCT_SECTION_TYPES.has(candidate.type) &&
      candidate.productId === productId &&
      (candidate.type === 'media_product' || candidate.type === 'product_plan'),
  );
  const repProduct = proposal.repProducts?.find((product) => product.productId === productId);
  return section?.title ?? repProduct?.name ?? humanizeId(productId);
}

/**
 * A one-channel `tactic_group` can exist in an older saved document or a
 * composition-plan fallback. In that case the page is about the channel,
 * not an invented umbrella concept, so its visible title is the product
 * name. Real multi-channel groups keep their authored shared headline.
 */
export function tacticGroupDisplayTitle(
  proposal: Proposal,
  productIds: string[],
  groupTitle: string,
  resolveProductName: (productId: string) => string | null,
): string {
  if (productIds.length !== 1) return groupTitle;
  const productId = productIds[0]!;
  return resolveProductName(productId) ?? productLabel(proposal, productId);
}

/**
 * The document section id for a channel's plan page — what the missing-price
 * note (GridWorkspace) focuses when a rep clicks an unpriced item, the same
 * way clicking a slide in the grid does. Null when the tactic has no plan
 * page yet (not yet built, or the whole document is missing).
 */
export function sectionIdForProduct(proposal: Proposal, productId: string): string | null {
  const section = proposal.document?.sections.find(
    (candidate) =>
      candidate.productId === productId &&
      (candidate.type === 'media_product' || candidate.type === 'product_plan'),
  );
  return section?.id ?? null;
}

/** Sections that should actually render, in document order. */
export function visibleSections(proposal: Proposal): DocumentSection[] {
  return proposal.document?.sections.filter((section) => section.hidden !== true) ?? [];
}

/**
 * The page chip's `PageRef` for a section (message-bar §2): the same 1-based
 * position `visibleSections` gives ProposalGallery's "Page N of M", and the
 * same display title PageCard shows under its thumbnail — so the chip never
 * disagrees with the grid it names. A hidden section (not part of the
 * visible sequence) or an id that doesn't resolve gets no `PageRef`, which
 * the caller reads as "no chip" rather than a stale or made-up position.
 */
export function resolvePageRef(
  proposal: Proposal,
  sectionId: string | null,
  resolveProductName: (productId: string) => string | null,
): PageRef | null {
  if (!sectionId) return null;
  const sections = visibleSections(proposal);
  const index = sections.findIndex((section) => section.id === sectionId);
  if (index === -1) return null;
  const section = sections[index]!;
  return {
    sectionId,
    title: tacticGroupDisplayTitle(proposal, section.productIds ?? [], section.title, resolveProductName),
    number: index + 1,
  };
}

/**
 * The reference deck's 6-stage funnel (design.md V5.1: "REPLACE the 4-stage
 * circles with the reference's 6-stage FUNNEL"). Ordered top-to-bottom.
 */
export type JourneyStage =
  | 'branding_awareness'
  | 'interest_research'
  | 'consideration'
  | 'intent'
  | 'conversion'
  | 'retention';

/**
 * Which funnel stage a channel badges onto in the customer_journey diagram.
 * A structural, presentation-only categorization (not a commercial value) -
 * known channel ids map explicitly; anything else falls back on its pricing
 * model, which is at least a fact already in state. Every catalog product
 * maps to exactly one stage, so every selected channel is guaranteed to
 * appear in at least one band.
 */
const STAGE_BY_PRODUCT: Record<string, JourneyStage> = {
  ctv: 'branding_awareness',
  online_video: 'branding_awareness',
  streaming_audio: 'branding_awareness',
  social: 'interest_research',
  targeted_display: 'consideration',
  mobile_location: 'intent',
  paid_search: 'conversion',
  email: 'retention',
};

/** V16: family-keyed fallback, same reasoning as DEFAULT_TACTIC_ROLE_BY_FAMILY above. */
const STAGE_BY_FAMILY: Partial<Record<ProductFamily, JourneyStage>> = {
  ctv: 'branding_awareness',
  online_video: 'branding_awareness',
  streaming_audio: 'branding_awareness',
  social: 'interest_research',
  display: 'consideration',
  mobile_location: 'intent',
  search: 'conversion',
  email: 'retention',
  dooh: 'branding_awareness',
  web_services: 'consideration',
};

export function journeyStage(
  productId: string,
  kind: DeliveryKind,
  family?: ProductFamily | null,
): JourneyStage {
  const known = STAGE_BY_PRODUCT[productId] ?? (family ? STAGE_BY_FAMILY[family] : undefined);
  if (known) return known;
  if (kind === 'cpc') return 'conversion';
  if (kind === 'flat') return 'retention';
  return 'consideration';
}

/**
 * Which standard-family funnel stage each selected channel badges onto, and
 * the display labels for the channels in that stage — the same computation
 * CustomerJourneyPage runs inline, exported so the new campaign_architecture
 * archetype (design.md V12: "funnel stage columns … with tactic chips per
 * stage") can share it rather than re-deriving the mapping. Mirrors
 * momentum's `tacticsByStage` in pages/momentum-v3/Motifs.tsx.
 */
export function journeyStageBadges(
  proposal: Proposal,
  labelFor: (productId: string) => string,
): Map<JourneyStage, string[]> {
  const map = new Map<JourneyStage, string[]>();
  for (const line of proposal.mediaPlan.channels) {
    const priced = pricedLine(proposal, line.productId);
    const model = productPricingModel(proposal, line.productId);
    const kind = deliveryKind(priced, undefined, model);
    const stage = journeyStage(line.productId, kind, productFamily(proposal, line.productId));
    const label = labelFor(line.productId);
    const list = map.get(stage) ?? [];
    list.push(label);
    map.set(stage, list);
  }
  return map;
}

/**
 * Momentum's own FIVE-stage funnel (design.md V6 customer_journey:
 * AWARENESS / CONSIDERATION / INTENT / CONVERSION / RETENTION - matches the
 * n03 reference render's card tactics). Added beside the standard family's
 * six-stage `JourneyStage` above without changing it - each template family
 * owns its own mapping, both read the exact same plan state.
 */
export type MomentumJourneyStage = 'awareness' | 'consideration' | 'intent' | 'conversion' | 'retention';

const MOMENTUM_STAGE_BY_PRODUCT: Record<string, MomentumJourneyStage> = {
  ctv: 'awareness',
  online_video: 'awareness',
  streaming_audio: 'awareness',
  social: 'consideration',
  targeted_display: 'consideration',
  mobile_location: 'intent',
  paid_search: 'intent',
  email: 'retention',
};

/** V16: family-keyed fallback, same reasoning as DEFAULT_TACTIC_ROLE_BY_FAMILY above. */
const MOMENTUM_STAGE_BY_FAMILY: Partial<Record<ProductFamily, MomentumJourneyStage>> = {
  ctv: 'awareness',
  online_video: 'awareness',
  streaming_audio: 'awareness',
  social: 'consideration',
  display: 'consideration',
  mobile_location: 'intent',
  search: 'intent',
  email: 'retention',
  dooh: 'awareness',
  web_services: 'consideration',
};

export function momentumJourneyStage(
  productId: string,
  kind: DeliveryKind,
  family?: ProductFamily | null,
): MomentumJourneyStage {
  const known = MOMENTUM_STAGE_BY_PRODUCT[productId] ?? (family ? MOMENTUM_STAGE_BY_FAMILY[family] : undefined);
  if (known) return known;
  if (kind === 'cpc') return 'conversion';
  if (kind === 'flat') return 'retention';
  return 'consideration';
}

/**
 * K3e: `momentumJourneyStage` above is a fixed productId/family lookup - it
 * has no way to hear what the agent actually decided when it authored the
 * strategy (`set_tactic`'s `funnelStage`, prompted as "one display word,
 * e.g. 'Awareness'" - src/agent/prompt.ts). Aliases the handful of synonyms
 * an author might reasonably write onto momentum_v3's fixed 5-stage
 * vocabulary; anything unrecognized (or absent) returns null so the caller
 * falls back to the product-derived default instead of silently mis-sorting
 * a free-text stage into the wrong of 5 fixed cards.
 */
const MOMENTUM_STAGE_ALIASES: Record<string, MomentumJourneyStage> = {
  awareness: 'awareness',
  consideration: 'consideration',
  interest: 'consideration',
  intent: 'intent',
  demand: 'intent',
  conversion: 'conversion',
  action: 'conversion',
  purchase: 'conversion',
  sale: 'conversion',
  retention: 'retention',
  loyalty: 'retention',
};

export function normalizeMomentumStage(raw: string | null | undefined): MomentumJourneyStage | null {
  if (!raw) return null;
  return MOMENTUM_STAGE_ALIASES[raw.trim().toLowerCase()] ?? null;
}

/**
 * The stage a channel badges onto for momentum_v3's fixed 5-card journey
 * (`JourneyPages.tsx`'s `CustomerJourneyPage`, `Motifs.tsx`'s
 * `tacticsByStage`): the tactic's own authored `funnelStage` when it matches
 * the vocabulary (`normalizeMomentumStage`), else the product/delivery-kind
 * default (`momentumJourneyStage`). `CampaignArchitecturePage` already
 * favors `tactic.funnelStage` this way for its own (unbucketed, free-text)
 * stage column; this gives the fixed 5-stage journey slide the same
 * authored-strategy-wins behavior instead of silently overriding it with
 * the generic product table.
 */
export function resolvedMomentumStage(
  proposal: Proposal,
  productId: string,
  kind: DeliveryKind,
  family?: ProductFamily | null,
): MomentumJourneyStage {
  const tactic = tacticFor(proposal, productId);
  return normalizeMomentumStage(tactic?.funnelStage) ?? momentumJourneyStage(productId, kind, family);
}

/** "MM/DD/YYYY" - Intl already orders en-US date parts this way. */
const usDate = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * "MM/DD/YYYY - MM/DD/YYYY" for the whole flight: budget.startDate through
 * startDate + durationMonths, formatting only (date math already in libs, no
 * commercial arithmetic). Null when no start date is set.
 */
export function flightDateRange(proposal: Proposal): string | null {
  const { startDate, durationMonths, endDate } = proposal.budget;
  if (!startDate) return null;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return null;
  const months = durationMonths > 0 ? durationMonths : 1;
  const startUtc = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const explicitEnd = endDate ? new Date(endDate) : null;
  const endUtc = explicitEnd && !Number.isNaN(explicitEnd.getTime())
    ? new Date(Date.UTC(explicitEnd.getUTCFullYear(), explicitEnd.getUTCMonth(), explicitEnd.getUTCDate()))
    : new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, start.getUTCDate() - 1));
  return `${usDate.format(startUtc)} - ${usDate.format(endUtc)}`;
}

/**
 * "MM/DD/YYYY - MM/DD/YYYY" for one calendar month of the flight schedule
 * (1-based monthIndex, same indexing as MonthlyScheduleRow) - the same
 * month-offset arithmetic monthLabel() already performs, just carried through
 * to a full range instead of a "Month Year" label.
 */
export function monthDateRange(startDate: string | null | undefined, monthIndex: number): string | null {
  if (!startDate) return null;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  const monthStart = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + (monthIndex - 1), 1));
  const monthEnd = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + monthIndex, 0));
  return `${usDate.format(monthStart)} - ${usDate.format(monthEnd)}`;
}

export interface PageGroup {
  /** Present when every section in the group shares one productId - a media bundle. */
  productId: string | null;
  sections: DocumentSection[];
}

/**
 * Groups consecutive document sections that share a productId (the V5 product
 * bundle triple - divider/education/plan - or a single legacy media_product
 * page) so the grid can cluster them visually. Every other section is its own
 * group of one. Order-preserving; works on any section list (visible or all).
 */
export function pageGroups(sections: DocumentSection[]): PageGroup[] {
  const groups: PageGroup[] = [];
  for (const section of sections) {
    const last = groups[groups.length - 1];
    if (
      section.productId &&
      last &&
      last.productId === section.productId &&
      PRODUCT_SECTION_TYPES.has(section.type)
    ) {
      last.sections.push(section);
    } else {
      groups.push({ productId: section.productId ?? null, sections: [section] });
    }
  }
  return groups;
}
